import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Scissors, Trash2, GripVertical, Image, ZoomIn, ZoomOut,
  Play, Pause, ChevronLeft, ChevronRight, Volume2, VolumeX,
  Eye, EyeOff, Copy, Clipboard, RotateCcw, Magnet, Type, Plus,
  Gauge, Minus, Subtitles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// ── Helpers ─────────────────────────────────────────────────────────────────

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Sort video clips by startTime and pack them with no gaps (ripple) */
const packClips = (clips) => {
  const sorted = [...clips].sort((a, b) => a.startTime - b.startTime);
  let cursor = 0;
  return sorted.map(clip => {
    const len = clip.endTime - clip.startTime;
    const result = { ...clip, startTime: cursor, endTime: cursor + len };
    cursor += len;
    return result;
  });
};

/** Re-sync subtitle tracks to follow their video clips after a pack/reorder (magnet ON).
 *  Each subtitle's midpoint determines which old clip it belongs to. If that clip moved,
 *  the subtitle shifts by the same delta. Subtitles whose clip was deleted are removed. */
const syncSubtitlesToPack = (prevClips, packedClips, textTracks) => {
  if (!textTracks.length) return textTracks;
  const result = textTracks.map(track => {
    const mid = (track.startTime + track.endTime) / 2;
    const oldClip = prevClips.find(c => mid >= c.startTime && mid < c.endTime);
    if (!oldClip) return track; // not inside any clip, leave as-is
    const newClip = packedClips.find(c => c.id === oldClip.id);
    if (!newClip) return null; // clip deleted → remove subtitle
    const delta = newClip.startTime - oldClip.startTime;
    if (Math.abs(delta) < 0.001) return track;
    return { ...track, startTime: Math.round((track.startTime + delta) * 1000) / 1000, endTime: Math.round((track.endTime + delta) * 1000) / 1000 };
  }).filter(Boolean);
  return result;
};

/** Re-sync audio clips to match video clips (magnet ON) — preserves music tracks */
const syncAudioToVideo = (videoClips, prevAudio) => {
  const musicClips = prevAudio.filter(a => a.isMusicTrack); // never touch music clips
  const synced = videoClips.map(vc => {
    const existing = prevAudio.find(a => a.videoClipId === vc.id);
    return {
      id: `aud-${vc.id}`,
      videoClipId: vc.id,
      name: vc.name,
      startTime: vc.startTime,
      endTime: vc.endTime,
      volume: existing?.volume ?? 1,
      muted: existing?.muted ?? false,
      fadeIn: existing?.fadeIn ?? 0,
      fadeOut: existing?.fadeOut ?? 0,
    };
  });
  return [...synced, ...musicClips];
};

const TRANSITION_TYPES = ['cut', 'fade', 'dissolve', 'wipe'];
const TRANSITION_LABEL = { cut: '✕', fade: '⬡', dissolve: '◈', wipe: '▷' };
const TRANSITION_COLOR = { cut: '#555', fade: '#4f8ef7', dissolve: '#a855f7', wipe: '#22c55e' };

const fmtTime = (s) => {
  const m = Math.floor(s / 60), sec = Math.floor(s % 60), ms = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, '0')}.${ms}`;
};

// ── Component ────────────────────────────────────────────────────────────────

export const TimelinePanel = ({
  clips = [], onClipsChange,
  brollClips = [], onBrollChange,
  textTracks = [], onTextTracksChange,
  duration = 60, currentTime = 0, onTimeChange,
  onAddBroll, onAutoSubtitles, onTextSelectionChange,
  thumbnailBaseUrl = null,
  thumbnailStartTime = 0,
  videoId = null,
  videoOriginalStartTime = 0,
  isPlaying = false,
  onPlayToggle,
  musicTrack = null,          // {id, name, url, duration_seconds} | null
  onMusicClipChange = null,   // (clip | null) => void — notifies parent of music clip state
  className = '', style = {},
}) => {
  const BASE = process.env.REACT_APP_BACKEND_URL || '';
  // Use either the explicit thumbnailBaseUrl or compute from videoId
  const filmstripBase = thumbnailBaseUrl || (videoId ? `${BASE}/api/youtube/clip-thumbnail/${videoId}?time_seconds=` : null);
  const filmstripOffset = thumbnailBaseUrl ? thumbnailStartTime : videoOriginalStartTime;
  const tlRef = useRef(null);
  const pinchRef = useRef(null);
  const actionsRef = useRef({});
  const preDragClipsRef = useRef(null); // captures clip positions before drag starts

  // ── State ──────────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const [magnetOn, setMagnetOn] = useState(true);
  const [audioClips, setAudioClips] = useState(() => syncAudioToVideo(clips, []));
  const [transitions, setTransitions] = useState([]);

  const [selectedClipId, setSelectedClipId] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  // isPlaying is now controlled by VideoEditorPage (passed as prop)
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [showBroll, setShowBroll] = useState(false);
  const [history, setHistory] = useState([]);
  const [clipboard, setClipboard] = useState(null);

  const [tracks, setTracks] = useState({
    video: { dimmed: false, locked: false },
    broll: { dimmed: false, locked: false },
    audio: { dimmed: false, muted: false },
    text: { dimmed: false, locked: false },
  });

  const pxPerSec = 12 * zoom;
  const tlWidth = Math.max(duration * pxPerSec, 600);

  // ── Sync audio when clips change (magnet ON) ───────────────────────────────
  useEffect(() => {
    if (magnetOn) setAudioClips(prev => syncAudioToVideo(clips, prev));
  }, [clips, magnetOn]);

  // ── Inject / remove music clip when musicTrack prop changes ────────────────
  useEffect(() => {
    setAudioClips(prev => {
      const without = prev.filter(c => !c.isMusicTrack);
      if (!musicTrack) return without;
      // Preserve existing music clip position/volume if same track
      const existing = prev.find(c => c.isMusicTrack && c.sourceId === musicTrack.id);
      return [...without, existing || {
        id: `music-${musicTrack.id}`,
        name: musicTrack.name,
        startTime: 0,
        endTime: musicTrack.duration_seconds || duration,
        volume: 0.7,
        muted: false,
        isMusicTrack: true,
        sourceId: musicTrack.id,
        sourceUrl: musicTrack.url,
        videoClipId: null,
      }];
    });
  }, [musicTrack, duration]);

  // ── Notify parent when music clip changes (position / volume) ──────────────
  useEffect(() => {
    const musicClip = audioClips.find(c => c.isMusicTrack) || null;
    onMusicClipChange?.(musicClip);
  }, [audioClips, onMusicClipChange]);

  // ── Refs for stable keyboard callbacks ────────────────────────────────────
  const clipsRef = useRef(clips);
  const brollRef = useRef(brollClips);
  const audioRef = useRef(audioClips);
  const textTracksRef = useRef(textTracks);
  const selectedRef = useRef(selectedClipId);
  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => { clipsRef.current = clips; }, [clips]);
  useEffect(() => { brollRef.current = brollClips; }, [brollClips]);
  useEffect(() => { audioRef.current = audioClips; }, [audioClips]);
  useEffect(() => { textTracksRef.current = textTracks; }, [textTracks]);
  useEffect(() => { selectedRef.current = selectedClipId; }, [selectedClipId]);
  useEffect(() => { selectedTrackRef.current = selectedTrack; }, [selectedTrack]);

  // ── History ────────────────────────────────────────────────────────────────
  const pushHistory = useCallback(() => {
    setHistory(h => [...h.slice(-20), {
      clips: [...clipsRef.current],
      brollClips: [...brollRef.current],
      audioClips: [...audioRef.current],
      textTracks: [...textTracksRef.current],
    }]);
  }, []);

  // ── Pack + finalize (called after drag / cut) ─────────────────────────────
  const finalizeClips = useCallback((newClips, newAudio, prevClipsOverride) => {
    if (magnetOn) {
      const prevClips = prevClipsOverride ?? [...clipsRef.current];
      const packed = packClips(newClips);
      onClipsChange?.(packed);
      setAudioClips(syncAudioToVideo(packed, newAudio ?? audioRef.current));
      // Sync subtitles: shift them to follow their video clips
      const syncedSubs = syncSubtitlesToPack(prevClips, packed, textTracksRef.current);
      if (syncedSubs !== textTracksRef.current) onTextTracksChange?.(syncedSubs);
    } else {
      onClipsChange?.(newClips);
      if (newAudio) setAudioClips(newAudio);
    }
  }, [magnetOn, onClipsChange, onTextTracksChange]);

  // ── Cut ───────────────────────────────────────────────────────────────────
  const handleCut = useCallback(() => {
    const cl = clipsRef.current;
    const id = selectedRef.current;
    const track = selectedTrackRef.current;
    if (!id) { toast.error('Selecciona un clip para cortar'); return; }

    if (track === 'video' || !track) {
      const idx = cl.findIndex(c => c.id === id);
      if (idx === -1) return;
      const clip = cl[idx];
      if (currentTime <= clip.startTime || currentTime >= clip.endTime) {
        toast.error('Mueve el cursor al interior del clip'); return;
      }
      pushHistory();
      const a = { ...clip, id: `${clip.id}-a`, endTime: currentTime };
      const b = { ...clip, id: `${clip.id}-b`, startTime: currentTime };
      const next = [...cl]; next.splice(idx, 1, a, b);
      // Cut audio too
      const audIdx = audioRef.current.findIndex(a => a.videoClipId === id);
      let nextAudio = [...audioRef.current];
      if (audIdx !== -1) {
        const aud = nextAudio[audIdx];
        nextAudio.splice(audIdx, 1,
          { ...aud, id: `aud-${clip.id}-a`, videoClipId: `${clip.id}-a`, endTime: currentTime },
          { ...aud, id: `aud-${clip.id}-b`, videoClipId: `${clip.id}-b`, startTime: currentTime }
        );
      }
      finalizeClips(next, nextAudio);
      setSelectedClipId(b.id);
      toast.success('Clip dividido');
    } else if (track === 'broll') {
      const br = brollRef.current;
      const idx = br.findIndex(c => c.id === id);
      if (idx === -1) return;
      const clip = br[idx];
      if (currentTime <= clip.startTime || currentTime >= clip.endTime) {
        toast.error('Mueve el cursor al interior del clip'); return;
      }
      pushHistory();
      const a = { ...clip, id: `${clip.id}-a`, endTime: currentTime };
      const b = { ...clip, id: `${clip.id}-b`, startTime: currentTime };
      const next = [...br]; next.splice(idx, 1, a, b);
      onBrollChange?.(next);
      setSelectedClipId(b.id);
      toast.success('B-Roll dividido');
    }
  }, [currentTime, pushHistory, finalizeClips, onBrollChange]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(() => {
    const id = selectedRef.current;
    const track = selectedTrackRef.current;
    if (!id) return;
    pushHistory();
    if (track === 'broll') {
      onBrollChange?.(brollRef.current.filter(c => c.id !== id));
    } else if (track === 'audio') {
      setAudioClips(prev => prev.filter(a => a.id !== id));
    } else if (track === 'text') {
      onTextTracksChange?.(textTracks.filter(t => t.id !== id));
    } else {
      const next = clipsRef.current.filter(c => c.id !== id);
      const nextAudio = audioRef.current.filter(a => a.videoClipId !== id);
      finalizeClips(next, nextAudio);
    }
    setSelectedClipId(null);
    toast.success('Eliminado');
  }, [pushHistory, onBrollChange, finalizeClips]);

  // ── Undo ──────────────────────────────────────────────────────────────────
  const handleUndo = useCallback(() => {
    if (history.length === 0) { toast.info('Nada que deshacer'); return; }
    const last = history[history.length - 1];
    onClipsChange?.(last.clips);
    onBrollChange?.(last.brollClips);
    setAudioClips(last.audioClips);
    onTextTracksChange?.(last.textTracks ?? []);
    setHistory(h => h.slice(0, -1));
    toast.success('Deshacer');
  }, [history, onClipsChange, onBrollChange, onTextTracksChange]);

  // ── Copy / Paste ──────────────────────────────────────────────────────────
  const handleCopy = useCallback(() => {
    const id = selectedRef.current;
    const track = selectedTrackRef.current;
    if (!id) return;
    const list = track === 'broll' ? brollRef.current : clipsRef.current;
    const clip = list.find(c => c.id === id);
    if (clip) { setClipboard({ clip, track }); toast.success('Copiado'); }
  }, []);

  const handlePaste = useCallback(() => {
    if (!clipboard) return;
    pushHistory();
    const len = clipboard.clip.endTime - clipboard.clip.startTime;
    const newClip = { ...clipboard.clip, id: `${clipboard.clip.id}-p${Date.now()}`, startTime: currentTime, endTime: currentTime + len };
    if (clipboard.track === 'broll') {
      onBrollChange?.([...brollRef.current, newClip]);
    } else {
      finalizeClips([...clipsRef.current, newClip], null);
    }
    setSelectedClipId(newClip.id);
    toast.success('Pegado');
  }, [clipboard, currentTime, pushHistory, finalizeClips, onBrollChange]);

  // ── Speed ─────────────────────────────────────────────────────────────────
  const setClipSpeed = (id, speed) => {
    const idx = clipsRef.current.findIndex(c => c.id === id);
    if (idx === -1) return;
    const clip = clipsRef.current[idx];
    const originalLen = (clip.endTime - clip.startTime) * (clip.speed || 1);
    const newEnd = clip.startTime + originalLen / speed;
    const next = [...clipsRef.current];
    next[idx] = { ...clip, speed, endTime: newEnd };
    finalizeClips(next, null);
  };

  // ── Transitions ───────────────────────────────────────────────────────────
  const cycleTransition = (afterClipId) => {
    setTransitions(prev => {
      const existing = prev.find(t => t.afterClipId === afterClipId);
      if (!existing) return [...prev, { id: `t-${afterClipId}`, afterClipId, type: 'fade', duration: 0.5 }];
      const nextType = TRANSITION_TYPES[(TRANSITION_TYPES.indexOf(existing.type) + 1) % TRANSITION_TYPES.length];
      if (nextType === 'cut') return prev.filter(t => t.afterClipId !== afterClipId);
      return prev.map(t => t.afterClipId === afterClipId ? { ...t, type: nextType } : t);
    });
  };

  // ── Keyboard ──────────────────────────────────────────────────────────────
  actionsRef.current = { handleCut, handleDelete, handleUndo, handleCopy, handlePaste, playToggle: onPlayToggle };
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      const a = actionsRef.current;
      switch (e.code) {
        case 'Space': e.preventDefault(); a.playToggle?.(); break;
        case 'KeyB': if (e.ctrlKey || e.metaKey) { e.preventDefault(); a.handleCut(); } break;
        case 'Delete': case 'Backspace': if (selectedRef.current) { e.preventDefault(); a.handleDelete(); } break;
        case 'KeyZ': if (e.ctrlKey || e.metaKey) { e.preventDefault(); a.handleUndo(); } break;
        case 'KeyC': if (e.ctrlKey || e.metaKey) { e.preventDefault(); a.handleCopy(); } break;
        case 'KeyV': if (e.ctrlKey || e.metaKey) { e.preventDefault(); a.handlePaste(); } break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Playback — controlled externally via isPlaying prop + onPlayToggle ─────
  // (No local setInterval — the video element drives time via rAF in VideoPreviewPanel)

  // ── Seek ──────────────────────────────────────────────────────────────────
  const handleTlClick = (e) => {
    if (isDragging) return;
    const rect = tlRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left + (tlRef.current?.scrollLeft || 0);
    onTimeChange?.(clamp(x / pxPerSec, 0, duration));
  };

  // ── Drag ─────────────────────────────────────────────────────────────────
  const handleDragStart = (e, clipId, type, track) => {
    e.stopPropagation();
    pushHistory();
    // Capture clip positions BEFORE any drag movement (used for subtitle sync on drop)
    if (track === 'video' || !track) preDragClipsRef.current = [...clipsRef.current];
    const rect = tlRef.current?.getBoundingClientRect();
    const x = e.clientX - (rect?.left || 0) + (tlRef.current?.scrollLeft || 0);
    const allClips = track === 'broll' ? brollRef.current : track === 'audio' ? audioRef.current : clipsRef.current;
    const clip = allClips.find(c => c.id === clipId);
    setDragOffset(clip ? x / pxPerSec - clip.startTime : 0);
    setIsDragging(true);
    setDragType(type);
    setSelectedClipId(clipId);
    setSelectedTrack(track);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !tlRef.current) return;
    const rect = tlRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + tlRef.current.scrollLeft;
    const time = x / pxPerSec;

    const updateList = (list, setter, afterUpdate) => {
      const idx = list.findIndex(c => c.id === selectedRef.current);
      if (idx === -1) return;
      const clip = list[idx];
      const next = [...list];
      if (dragType === 'resize-left') {
        next[idx] = { ...clip, startTime: clamp(time, 0, clip.endTime - 0.5) };
      } else if (dragType === 'resize-right') {
        next[idx] = { ...clip, endTime: clamp(time, clip.startTime + 0.5, duration * 2) };
      } else if (dragType === 'move') {
        const len = clip.endTime - clip.startTime;
        // For video clips, allow wide range so reorder/pack works on drop
        const start = clamp(time - dragOffset, -len * 0.5, duration * 1.5);
        next[idx] = { ...clip, startTime: start, endTime: start + len };
      }
      setter(next);
      afterUpdate?.(next);
    };

    const track = selectedTrackRef.current;
    if (track === 'broll') {
      updateList(brollRef.current, onBrollChange);
    } else if (track === 'audio') {
      updateList(audioRef.current, setAudioClips);
    } else {
      updateList(clipsRef.current, (next) => {
        if (magnetOn && dragType === 'move') {
          // Ripple effect: pack clips in real-time during drag so others push aside
          const packed = packClips(next);
          onClipsChange?.(packed);
          setAudioClips(syncAudioToVideo(packed, audioRef.current));
        } else {
          onClipsChange?.(next);
          if (magnetOn) setAudioClips(syncAudioToVideo(next, audioRef.current));
        }
      });
    }
  }, [isDragging, dragType, dragOffset, pxPerSec, duration, magnetOn, onClipsChange, onBrollChange]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
    // Ripple pack on drag end if magnet ON (video track)
    if (magnetOn && (selectedTrackRef.current === 'video' || !selectedTrackRef.current)) {
      const prevClips = preDragClipsRef.current ?? clipsRef.current;
      const packed = packClips(clipsRef.current);
      onClipsChange?.(packed);
      setAudioClips(syncAudioToVideo(packed, audioRef.current));
      // Sync subtitles to follow their clips
      const syncedSubs = syncSubtitlesToPack(prevClips, packed, textTracksRef.current);
      onTextTracksChange?.(syncedSubs);
      preDragClipsRef.current = null;
    }
    // Always pack audio clips to prevent audio-audio overlaps
    if (selectedTrackRef.current === 'audio') {
      setAudioClips(prev => packClips(prev));
    }
  }, [magnetOn, onClipsChange, onTextTracksChange]);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleMouseMove, handleDragEnd]);

  // ── Pinch zoom ───────────────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) pinchRef.current = {
      dist: Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY), zoom,
    };
  };
  const handleTouchMove = (e) => {
    if (e.touches.length !== 2 || !pinchRef.current) return;
    e.preventDefault();
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    setZoom(Math.round(clamp(pinchRef.current.zoom * d / pinchRef.current.dist, 0.5, 4) * 4) / 4);
  };

  // ── Selected clip info ────────────────────────────────────────────────────
  const selectedClip = useMemo(() => {
    const all = [...clipsRef.current, ...brollRef.current, ...audioRef.current, ...textTracks];
    return all.find(c => c.id === selectedClipId) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId, clips, brollClips, audioClips, textTracks]);

  const toggleTrack = (id, prop) => setTracks(t => ({ ...t, [id]: { ...t[id], [prop]: !t[id][prop] } }));

  // ── Clip render helper ────────────────────────────────────────────────────
  const renderClip = (clip, track, color, height) => {
    const isSelected = selectedClipId === clip.id;
    const locked = tracks[track]?.locked;
    const left = clip.startTime * pxPerSec;
    const width = Math.max((clip.endTime - clip.startTime) * pxPerSec, 8);
    const fadeInW = (clip.fadeIn || 0) * pxPerSec;
    const fadeOutW = (clip.fadeOut || 0) * pxPerSec;
    const clipDuration = clip.endTime - clip.startTime;

    // Filmstrip thumbnails for video track — skip during drag (prevents continuous thumbnail requests)
    const isBeingDragged = isDragging && clip.id === selectedClipId;
    const filmstrip = track === 'video' && filmstripBase && width > 20 && !isBeingDragged ? (() => {
      const thumbW = 44; // px per thumbnail frame
      const count = Math.max(1, Math.min(20, Math.ceil(width / thumbW)));
      const interval = clipDuration / count;
      return Array.from({ length: count }, (_, i) => {
        const relTime = i * interval + interval / 2;
        // clip.startTime is the clip's position in the timeline (relative to clip preview start)
        const absTime = (filmstripOffset + clip.startTime + relTime).toFixed(1);
        return (
          <img
            key={i}
            src={`${filmstripBase}${absTime}`}
            alt=""
            style={{ width: thumbW, flexShrink: 0, height: '100%', objectFit: 'cover', display: 'block' }}
          />
        );
      });
    })() : null;

    return (
      <div key={clip.id}
        style={{
          position: 'absolute', top: 3, bottom: 3, left, width, borderRadius: 5,
          background: filmstrip ? '#111' : color,
          outline: isSelected ? '2px solid hsl(var(--primary))' : 'none',
          outlineOffset: 1, opacity: locked ? 0.55 : 1, pointerEvents: locked ? 'none' : 'auto',
          overflow: 'hidden', cursor: 'pointer',
        }}
        onClick={(e) => { e.stopPropagation(); setSelectedClipId(clip.id); setSelectedTrack(track); onTextSelectionChange?.(null); }}
      >
        {/* Filmstrip for video track */}
        {filmstrip && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', overflow: 'hidden' }}>
            {filmstrip}
          </div>
        )}
        {/* Fade in gradient */}
        {fadeInW > 0 && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: fadeInW, background: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />}
        {/* Fade out gradient */}
        {fadeOutW > 0 && <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: fadeOutW, background: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)', pointerEvents: 'none' }} />}

        {/* Resize left */}
        {!locked && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 5 }} onMouseDown={e => handleDragStart(e, clip.id, 'resize-left', track)} />}
        {/* Resize right */}
        {!locked && <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 5 }} onMouseDown={e => handleDragStart(e, clip.id, 'resize-right', track)} />}

        {/* Label overlay */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: '0 8px 2px', cursor: 'grab', overflow: 'hidden', background: filmstrip ? 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 60%)' : 'transparent' }}
          onMouseDown={!locked ? e => handleDragStart(e, clip.id, 'move', track) : undefined}>
          {!filmstrip && <GripVertical size={10} style={{ flexShrink: 0, opacity: 0.5, color: '#fff' }} />}
          <span style={{ fontSize: 10, marginLeft: filmstrip ? 0 : 3, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {clip.name || clip.category || 'Clip'}
            {clip.speed && clip.speed !== 1 ? ` ×${clip.speed}` : ''}
          </span>
        </div>

        {/* Muted indicator for audio clips */}
        {(track === 'audio' && clip.muted) && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <VolumeX size={10} style={{ color: '#ff4444' }} />
          </div>
        )}

        {/* Stripe texture (only when no filmstrip) */}
        {!filmstrip && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.1, background: 'repeating-linear-gradient(90deg,transparent,transparent 8px,rgba(255,255,255,0.4) 8px,rgba(255,255,255,0.4) 9px)' }} />}
      </div>
    );
  };

  // ── Playhead line helper ──────────────────────────────────────────────────
  const playheadLine = (
    <div style={{ position: 'absolute', top: 0, bottom: 0, width: 1, left: currentTime * pxPerSec, background: 'hsl(var(--destructive))', zIndex: 10, pointerEvents: 'none' }} />
  );

  // ── Sorted clips for transition badges ───────────────────────────────────
  const sortedClips = useMemo(() => [...clips].sort((a, b) => a.startTime - b.startTime), [clips]);

  // ── Add text overlay ─────────────────────────────────────────────────────
  const addTextOverlay = () => {
    const t = { id: `txt-${Date.now()}`, name: 'Texto', text: 'Texto aquí', startTime: currentTime, endTime: currentTime + 3, position: { x: 50, y: 40 }, color: '#ffffff', fontSize: 18, fontFamily: 'default', isSubtitle: false };
    onTextTracksChange?.([...textTracks, t]);
    setSelectedClipId(t.id);
    setSelectedTrack('text');
  };

  // ── Markers ───────────────────────────────────────────────────────────────
  const markers = useMemo(() => {
    const interval = pxPerSec >= 60 ? 1 : pxPerSec >= 24 ? 5 : pxPerSec >= 12 ? 10 : 30;
    const m = [];
    for (let t = 0; t <= duration; t += interval) m.push(t);
    return m;
  }, [pxPerSec, duration]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`flex flex-col ${className}`}
      style={{ background: 'hsl(var(--surface))', userSelect: 'none', ...style }}
      data-testid="timeline-panel">

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between px-2 py-1 border-b flex-shrink-0"
        style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Dividir (Ctrl+B)" onClick={() => actionsRef.current.handleCut()}><Scissors size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Eliminar (Supr)" onClick={() => actionsRef.current.handleDelete()} disabled={!selectedClipId}><Trash2 size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copiar (Ctrl+C)" onClick={() => actionsRef.current.handleCopy()} disabled={!selectedClipId}><Copy size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Pegar (Ctrl+V)" onClick={() => actionsRef.current.handlePaste()} disabled={!clipboard}><Clipboard size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Deshacer (Ctrl+Z)" onClick={() => actionsRef.current.handleUndo()} disabled={history.length === 0}><RotateCcw size={14} /></Button>
          <div className="w-px h-4 mx-1" style={{ background: 'hsl(var(--border))' }} />
          {/* Magnet toggle */}
          <Button
            variant={magnetOn ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2"
            title={magnetOn ? 'Imán ON — clips se ajustan solos (click para desactivar)' : 'Imán OFF — clips libres (click para activar)'}
            onClick={() => setMagnetOn(v => !v)}
          >
            <Magnet size={13} className={magnetOn ? 'text-primary' : ''} />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" title="B-Roll" onClick={() => { setShowBroll(v => !v); }}>
            <Image size={13} className="mr-1" /><span className="text-xs">B-Roll</span>
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" title="Añadir texto" onClick={addTextOverlay}>
            <Type size={13} className="mr-1" /><span className="text-xs">Texto</span>
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" title="Subtítulos automáticos" onClick={() => onAutoSubtitles?.()}>
            <Subtitles size={13} className="mr-1" /><span className="text-xs">Subs</span>
          </Button>
        </div>

        {/* Playback */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onTimeChange?.(Math.max(0, currentTime - 5))}><ChevronLeft size={14} /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Play/Pausa (Espacio)" onClick={onPlayToggle} data-testid="timeline-play-btn">
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onTimeChange?.(Math.min(duration, currentTime + 5))}><ChevronRight size={14} /></Button>
          <span className="text-xs font-mono tabular-nums px-1" style={{ color: 'hsl(var(--foreground-muted))' }}>
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 4) / 4))}><ZoomOut size={14} /></Button>
          <span className="text-xs w-10 text-center tabular-nums" style={{ color: 'hsl(var(--foreground-muted))' }}>{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.min(4, Math.round((z + 0.25) * 4) / 4))}><ZoomIn size={14} /></Button>
        </div>
      </div>

      {/* ── Clip properties bar (when clip selected) ── */}
      {selectedClip && selectedTrack === 'video' && (
        <div className="flex items-center gap-4 px-3 py-1.5 border-b flex-shrink-0"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-elevated))' }}>
          <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>
            {selectedClip.name}
          </span>
          {/* Speed */}
          <div className="flex items-center gap-1">
            <Gauge size={12} style={{ color: 'hsl(var(--foreground-muted))' }} />
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>Velocidad:</span>
            {[0.5, 0.75, 1, 1.5, 2].map(s => (
              <button key={s} onClick={() => setClipSpeed(selectedClip.id, s)}
                className="text-xs px-1.5 py-0.5 rounded transition-colors"
                style={{
                  background: (selectedClip.speed || 1) === s ? 'hsl(var(--primary))' : 'hsl(var(--surface))',
                  color: (selectedClip.speed || 1) === s ? '#fff' : 'hsl(var(--foreground-muted))',
                  border: '1px solid hsl(var(--border))',
                }}>
                {s}×
              </button>
            ))}
          </div>
          {/* Fade in/out */}
          <div className="flex items-center gap-1">
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>Fade In:</span>
            <button onClick={() => { const idx = clips.findIndex(c => c.id === selectedClip.id); if (idx === -1) return; const next = [...clips]; next[idx] = { ...next[idx], fadeIn: Math.max(0, (next[idx].fadeIn || 0) - 0.2) }; onClipsChange?.(next); }} className="h-5 w-5 flex items-center justify-center rounded" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}><Minus size={10} /></button>
            <span className="text-xs w-8 text-center tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>{((selectedClip.fadeIn || 0)).toFixed(1)}s</span>
            <button onClick={() => { const idx = clips.findIndex(c => c.id === selectedClip.id); if (idx === -1) return; const next = [...clips]; next[idx] = { ...next[idx], fadeIn: Math.min((next[idx].endTime - next[idx].startTime) / 2, (next[idx].fadeIn || 0) + 0.2) }; onClipsChange?.(next); }} className="h-5 w-5 flex items-center justify-center rounded" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}><Plus size={10} /></button>
            <span className="text-xs ml-2" style={{ color: 'hsl(var(--foreground-muted))' }}>Fade Out:</span>
            <button onClick={() => { const idx = clips.findIndex(c => c.id === selectedClip.id); if (idx === -1) return; const next = [...clips]; next[idx] = { ...next[idx], fadeOut: Math.max(0, (next[idx].fadeOut || 0) - 0.2) }; onClipsChange?.(next); }} className="h-5 w-5 flex items-center justify-center rounded" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}><Minus size={10} /></button>
            <span className="text-xs w-8 text-center tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>{((selectedClip.fadeOut || 0)).toFixed(1)}s</span>
            <button onClick={() => { const idx = clips.findIndex(c => c.id === selectedClip.id); if (idx === -1) return; const next = [...clips]; next[idx] = { ...next[idx], fadeOut: Math.min((next[idx].endTime - next[idx].startTime) / 2, (next[idx].fadeOut || 0) + 0.2) }; onClipsChange?.(next); }} className="h-5 w-5 flex items-center justify-center rounded" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}><Plus size={10} /></button>
          </div>
        </div>
      )}

      {/* ── Audio clip properties ── */}
      {selectedClip && selectedTrack === 'audio' && (
        <div className="flex items-center gap-4 px-3 py-1.5 border-b flex-shrink-0"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-elevated))' }}>
          <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>Audio: {selectedClip.name}</span>
          <button onClick={() => setAudioClips(prev => prev.map(a => a.id === selectedClip.id ? { ...a, muted: !a.muted } : a))}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            {selectedClip.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            {selectedClip.muted ? 'Sin audio' : 'Con audio'}
          </button>
          <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
            {magnetOn ? '🔗 Enlazado al video' : '🔓 Libre (imán OFF)'}
          </span>
        </div>
      )}

      {/* ── B-Roll Library ── */}
      {showBroll && (
        <div className="px-3 py-2 border-b flex-shrink-0 flex items-center gap-3"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-elevated))' }}>
          <span className="text-xs font-medium flex-shrink-0" style={{ color: 'hsl(var(--foreground-muted))' }}>B-Roll:</span>
          <div className="flex gap-2 overflow-x-auto">
            {['Gym', 'Nature', 'City', 'Tech', 'Food', 'Travel', 'People'].map(cat => (
              <button key={cat} draggable onDragEnd={() => {
                const nb = { id: `brl-${Date.now()}`, name: cat, category: cat, startTime: currentTime, endTime: currentTime + 3 };
                onBrollChange?.([...brollRef.current, nb]);
                toast.success(`B-Roll "${cat}" añadido`);
              }} className="flex-shrink-0 flex flex-col items-center gap-1 px-2 py-1 rounded cursor-grab active:cursor-grabbing hover:opacity-80"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                <Image size={12} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{cat}</span>
              </button>
            ))}
            <Button variant="outline" size="sm" className="flex-shrink-0 h-auto py-1" onClick={() => onAddBroll?.()}><Plus size={12} /></Button>
          </div>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0 ml-auto" onClick={() => setShowBroll(false)}>×</Button>
        </div>
      )}

      {/* ── Timeline tracks ── */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Track labels */}
        <div className="w-20 flex-shrink-0 flex flex-col border-r" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex-shrink-0" style={{ height: 20, borderBottom: '1px solid hsl(var(--border))' }} />
          {/* Video label — always visible */}
          <div className="flex items-center justify-between px-2 flex-shrink-0 border-b" style={{ height: 52, borderColor: 'hsl(var(--border))', opacity: tracks.video.dimmed ? 0.4 : 1 }}>
            <span className="text-xs font-medium" style={{ color: 'hsl(var(--foreground))' }}>Video</span>
            <div className="flex">
              <button className="p-0.5" title={tracks.video.dimmed ? 'Mostrar' : 'Atenuar'} onClick={() => toggleTrack('video', 'dimmed')}>
                {tracks.video.dimmed ? <EyeOff size={10} style={{ color: 'hsl(var(--warning))' }} /> : <Eye size={10} style={{ color: 'hsl(var(--foreground-subtle))' }} />}
              </button>
            </div>
          </div>
          {/* B-Roll label */}
          <div className="flex items-center justify-between px-2 flex-shrink-0 border-b" style={{ height: 36, borderColor: 'hsl(var(--border))', opacity: tracks.broll.dimmed ? 0.4 : 1 }}>
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>B-Roll</span>
            <button className="p-0.5" title={tracks.broll.dimmed ? 'Mostrar' : 'Atenuar'} onClick={() => toggleTrack('broll', 'dimmed')}>
              {tracks.broll.dimmed ? <EyeOff size={10} style={{ color: 'hsl(var(--warning))' }} /> : <Eye size={10} style={{ color: 'hsl(var(--foreground-subtle))' }} />}
            </button>
          </div>
          {/* Audio label */}
          <div className="flex items-center justify-between px-2 flex-shrink-0 border-b" style={{ height: 36, borderColor: 'hsl(var(--border))', opacity: tracks.audio.dimmed ? 0.4 : 1 }}>
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
              Audio {magnetOn ? '🔗' : '🔓'}
            </span>
            <button className="p-0.5" onClick={() => toggleTrack('audio', 'muted')}>
              {tracks.audio.muted ? <VolumeX size={10} style={{ color: 'hsl(var(--destructive))' }} /> : <Volume2 size={10} style={{ color: 'hsl(var(--foreground-subtle))' }} />}
            </button>
          </div>
          {/* Text label */}
          {textTracks.length > 0 && (
            <div className="flex items-center justify-between px-2 flex-shrink-0" style={{ height: 28, opacity: tracks.text.dimmed ? 0.4 : 1 }}>
              <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>Texto</span>
              <button className="p-0.5" title={tracks.text.dimmed ? 'Mostrar' : 'Atenuar'} onClick={() => toggleTrack('text', 'dimmed')}>
                {tracks.text.dimmed ? <EyeOff size={10} style={{ color: 'hsl(var(--warning))' }} /> : <Eye size={10} style={{ color: 'hsl(var(--foreground-subtle))' }} />}
              </button>
            </div>
          )}
        </div>

        {/* Scrollable area */}
        <div ref={tlRef} className="flex-1 overflow-x-auto overflow-y-hidden"
          onClick={handleTlClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}>
          <div style={{ width: tlWidth, minWidth: '100%' }}>
            {/* Ruler */}
            <div style={{ height: 20, position: 'relative', borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--surface-elevated))' }}>
              {markers.map(t => (
                <div key={t} style={{ position: 'absolute', top: 0, left: t * pxPerSec, pointerEvents: 'none', userSelect: 'none' }}>
                  <div style={{ width: 1, height: 8, background: 'hsl(var(--border))' }} />
                  <span style={{ fontSize: 9, color: 'hsl(var(--foreground-subtle))', paddingLeft: 2, whiteSpace: 'nowrap' }}>{fmtTime(t)}</span>
                </div>
              ))}
              {/* Playhead — draggable */}
              <div style={{ position: 'absolute', top: 0, height: '100%', width: 1, left: currentTime * pxPerSec, background: 'hsl(var(--destructive))', zIndex: 20, pointerEvents: 'none' }}>
                <div
                  style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 14, height: 14, background: 'hsl(var(--destructive))', borderRadius: '50%', cursor: 'grab', pointerEvents: 'all', zIndex: 21 }}
                  onMouseDown={e => {
                    e.stopPropagation();
                    setIsScrubbing(true);
                    const onMove = (mv) => {
                      const rect = tlRef.current?.getBoundingClientRect();
                      if (!rect) return;
                      const x = mv.clientX - rect.left + (tlRef.current?.scrollLeft || 0);
                      onTimeChange?.(parseFloat(clamp(x / pxPerSec, 0, duration).toFixed(1)));
                    };
                    const onUp = () => { setIsScrubbing(false); window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}
                  title="Arrastra para buscar frame"
                />
                {/* Time tooltip while scrubbing */}
                {isScrubbing && (
                  <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', background: 'hsl(var(--destructive))', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 30 }}>
                    {fmtTime(currentTime)}
                  </div>
                )}
              </div>
            </div>

            {/* Video track — always rendered, opacity changes when dimmed */}
            <div style={{ height: 52, position: 'relative', borderBottom: '1px solid hsl(var(--border))', opacity: tracks.video.dimmed ? 0.25 : 1, filter: tracks.video.dimmed ? 'grayscale(1)' : 'none' }}>
                {clips.map(clip => renderClip(clip, 'video', 'hsl(var(--primary))', 52))}
                {playheadLine}

                {/* Drop indicator — shows where the dragged clip will land after pack */}
                {isDragging && dragType === 'move' && (selectedTrack === 'video' || !selectedTrack) && (() => {
                  const packed = packClips(clips);
                  const dragged = packed.find(c => c.id === selectedClipId);
                  if (!dragged) return null;
                  const indicatorX = dragged.startTime * pxPerSec;
                  return (
                    <div key="drop-indicator" style={{
                      position: 'absolute', top: 0, bottom: 0, left: indicatorX,
                      width: 3, background: 'hsl(var(--primary))', zIndex: 50,
                      pointerEvents: 'none', borderRadius: 2,
                      boxShadow: '0 0 8px 2px hsl(var(--primary) / 0.7)',
                    }} />
                  );
                })()}

                {/* Transitions between adjacent clips */}
                {sortedClips.map((clip, i) => {
                  if (i === 0) return null;
                  const prev = sortedClips[i - 1];
                  const trans = transitions.find(t => t.afterClipId === prev.id);
                  const x = prev.endTime * pxPerSec;
                  return (
                    <div key={`t-${prev.id}`}
                      title={`Transición: ${trans?.type || 'cut'} (click para cambiar)`}
                      onClick={e => { e.stopPropagation(); cycleTransition(prev.id); }}
                      style={{
                        position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
                        left: x, width: 16, height: 16, borderRadius: 3, zIndex: 15,
                        background: trans ? TRANSITION_COLOR[trans.type] : 'hsl(var(--border))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontSize: 9, color: '#fff', fontWeight: 700,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      }}>
                      {TRANSITION_LABEL[trans?.type || 'cut']}
                    </div>
                  );
                })}
              </div>

            {/* B-Roll track — always rendered, opacity changes when dimmed */}
            <div style={{ height: 36, position: 'relative', borderBottom: '1px solid hsl(var(--border))', background: 'rgba(0,0,0,0.05)', opacity: tracks.broll.dimmed ? 0.25 : 1, filter: tracks.broll.dimmed ? 'grayscale(1)' : 'none' }}>
              {brollClips.map(clip => renderClip(clip, 'broll', 'hsl(142 76% 36%)', 36))}
              {playheadLine}
            </div>

            {/* Audio track — always rendered; music clips (isMusicTrack) are draggable + have volume control */}
            <div style={{ height: 36, position: 'relative', borderBottom: '1px solid hsl(var(--border))', opacity: tracks.audio.dimmed ? 0.25 : 1, filter: tracks.audio.dimmed ? 'grayscale(1)' : 'none' }}>
              {audioClips.map(clip => {
                const isSelected = selectedClipId === clip.id;
                const left = clip.startTime * pxPerSec;
                const width = Math.max((clip.endTime - clip.startTime) * pxPerSec, 8);
                const isMusic = clip.isMusicTrack;
                const bgColor = isMusic
                  ? 'hsl(280 60% 42%)'
                  : clip.muted || tracks.audio.muted ? 'hsl(var(--border))' : 'hsl(220 50% 45%)';

                return (
                  <div key={clip.id}
                    style={{
                      position: 'absolute', top: 3, bottom: 3, left, width, borderRadius: 4,
                      background: bgColor,
                      outline: isSelected ? '2px solid hsl(var(--primary))' : 'none', outlineOffset: 1,
                      overflow: 'visible', cursor: 'grab',
                    }}
                    onClick={e => { e.stopPropagation(); setSelectedClipId(clip.id); setSelectedTrack('audio'); }}
                    onMouseDown={e => handleDragStart(e, clip.id, 'move', 'audio')}
                  >
                    {/* Waveform */}
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 2px', overflow: 'hidden', gap: 1, borderRadius: 4 }}>
                      {Array.from({ length: Math.min(Math.floor(width / 2), 200) }).map((_, i) => (
                        <div key={i} style={{
                          width: 1, flexShrink: 0, borderRadius: 1,
                          height: `${20 + Math.abs(Math.sin(i * (isMusic ? 0.4 : 0.5)) * (isMusic ? 55 : 60) + Math.sin(i * (isMusic ? 0.11 : 0.13)) * 30)}%`,
                          background: (clip.muted || tracks.audio.muted) ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.55)',
                        }} />
                      ))}
                    </div>

                    {/* Music label */}
                    {isMusic && (
                      <span style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', textShadow: '0 1px 2px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
                        ♫ {clip.name}
                      </span>
                    )}

                    {/* Resize handles (always available for music clips, for regular audio only when magnet OFF) */}
                    {(isMusic || !magnetOn) && (
                      <>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 5 }}
                          onMouseDown={e => { e.stopPropagation(); handleDragStart(e, clip.id, 'resize-left', 'audio'); }} />
                        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 5 }}
                          onMouseDown={e => { e.stopPropagation(); handleDragStart(e, clip.id, 'resize-right', 'audio'); }} />
                      </>
                    )}

                    {/* Volume slider — appears above the clip when a music clip is selected */}
                    {isMusic && isSelected && (
                      <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
                        style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 50, background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))', borderRadius: 8, padding: '8px 10px', minWidth: 180, boxShadow: '0 4px 16px rgba(0,0,0,0.35)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 10, color: 'hsl(var(--foreground-muted))', whiteSpace: 'nowrap' }}>Volumen música</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'hsl(var(--primary))', marginLeft: 'auto' }}>{Math.round((clip.volume ?? 0.7) * 100)}%</span>
                        </div>
                        <input type="range" min="0" max="1" step="0.05"
                          value={clip.volume ?? 0.7}
                          style={{ width: '100%', accentColor: 'hsl(var(--primary))' }}
                          onChange={e => {
                            const vol = parseFloat(e.target.value);
                            setAudioClips(prev => prev.map(a => a.id === clip.id ? { ...a, volume: vol } : a));
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <span style={{ fontSize: 10, color: 'hsl(var(--foreground-muted))' }}>Volumen vídeo</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#5b9cf6', marginLeft: 'auto' }}>
                            {Math.round((1 - (clip.volume ?? 0.7)) * 100 + 30)}%
                          </span>
                        </div>
                        <input type="range" min="0.3" max="1" step="0.05"
                          value={Math.max(0.3, 1 - (clip.volume ?? 0.7) + 0.3)}
                          style={{ width: '100%', accentColor: '#5b9cf6' }}
                          onChange={e => {
                            const videoVol = parseFloat(e.target.value);
                            setAudioClips(prev => prev.map(a => a.id === clip.id ? { ...a, videoVolume: videoVol } : a));
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {playheadLine}
            </div>

            {/* Text track — always rendered, opacity changes when dimmed */}
            {textTracks.length > 0 && (
              <div style={{ height: 28, position: 'relative', opacity: tracks.text.dimmed ? 0.25 : 1, filter: tracks.text.dimmed ? 'grayscale(1)' : 'none' }}>
                {textTracks.map(t => {
                  const left = t.startTime * pxPerSec;
                  const width = Math.max((t.endTime - t.startTime) * pxPerSec, 8);
                  return (
                    <div key={t.id}
                      style={{
                        position: 'absolute', top: 3, bottom: 3, left, width, borderRadius: 4,
                        background: t.isSubtitle ? 'hsl(200 80% 45%)' : 'hsl(40 90% 50%)',
                        outline: selectedClipId === t.id ? '2px solid hsl(var(--primary))' : 'none',
                        outlineOffset: 1, overflow: 'hidden', cursor: 'grab', display: 'flex', alignItems: 'center', padding: '0 6px',
                      }}
                      onClick={e => { e.stopPropagation(); setSelectedClipId(t.id); setSelectedTrack('text'); onTextSelectionChange?.(t); }}
                      onMouseDown={e => handleDragStart(e, t.id, 'move', 'text')}
                    >
                      <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.isSubtitle ? '♪' : 'T'}: {t.text}
                      </span>
                    </div>
                  );
                })}
                {playheadLine}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Status bar ── */}
      <div className="flex items-center justify-between px-3 py-1 border-t flex-shrink-0"
        style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-elevated))' }}>
        <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
          {clips.length} clips · {brollClips.length} B-Roll · {textTracks.length} texto · imán {magnetOn ? 'ON' : 'OFF'}
        </span>
        <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
          {selectedClipId ? `Supr=borrar · Ctrl+B=cortar · Ctrl+Z=deshacer` : 'Espacio=play · Ctrl+C/V=copiar · click=seleccionar'}
        </span>
        <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{fmtTime(duration)}</span>
      </div>
    </div>
  );
};

export default TimelinePanel;
